import { useState, useEffect } from "react";
import api from "../../lib/api";
import { AlertTriangle, Users, Copy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AccessBlocked } from "../../components/AccessBlocked";

export default function CRMExceptionsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [blocked, setBlocked] = useState("");

  useEffect(() => {
    api.get("/crm/exceptions")
      .then(r => setItems(r.data || []))
      .catch(e => {
        // FX3b item 8 — plain English on a 403, never the permission key
        if (e.response?.status === 403) { setBlocked(e.response?.data?.detail || "forbidden"); return; }
        setErr(e.response?.data?.detail || "Failed to load exceptions");
      })
      .finally(() => setLoading(false));
  }, []);

  if (blocked) return <div className="p-8"><AccessBlocked detail={blocked} testid="crm-access-blocked" /></div>;
  if (err) return <div className="p-8"><p className="text-zinc-500 text-sm">{err}</p></div>;

  const noAction = items.filter(i => i.type === "no_next_action");
  const dupes = items.filter(i => i.type === "possible_duplicate");

  return (
    <div data-testid="crm-exceptions-page">
      <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 mb-1">Exceptions</h1>
      <p className="text-sm text-zinc-500 mb-4">{items.length} items need attention</p>

      {loading ? <p className="text-xs text-zinc-400">Loading...</p> : (
        <div className="space-y-6">
          {noAction.length > 0 && (
            <div className="border border-amber-200 rounded-sm bg-amber-50/50" data-testid="no-action-section">
              <div className="px-4 py-2 border-b border-amber-200 flex items-center gap-2">
                <AlertTriangle size={14} className="text-amber-600" />
                <h2 className="text-sm font-semibold text-amber-800">No Next Action ({noAction.length})</h2>
              </div>
              {noAction.map(item => (
                <div key={item.party_id} className="px-4 py-2 border-b border-amber-100 last:border-0 flex items-center gap-3 cursor-pointer hover:bg-amber-50"
                  onClick={() => navigate(`/crm/party/${item.party_id}`)}>
                  <p className="text-xs font-medium flex-1">{item.name} <span className="text-zinc-400">· {item.vertical}</span></p>
                </div>
              ))}
            </div>
          )}

          {dupes.length > 0 && (
            <div className="border border-blue-200 rounded-sm bg-blue-50/50" data-testid="dupes-section">
              <div className="px-4 py-2 border-b border-blue-200 flex items-center gap-2">
                <Copy size={14} className="text-blue-600" />
                <h2 className="text-sm font-semibold text-blue-800">Possible Duplicates ({dupes.length})</h2>
              </div>
              {dupes.map(item => (
                <div key={item.party_id} className="px-4 py-2 border-b border-blue-100 last:border-0 cursor-pointer hover:bg-blue-50"
                  onClick={() => navigate(`/crm/party/${item.party_id}`)}>
                  <p className="text-xs font-medium">{item.name} <span className="text-zinc-400">· {item.vertical}</span></p>
                </div>
              ))}
            </div>
          )}

          {items.length === 0 && <p className="text-center text-zinc-400 text-xs py-8 border border-dashed border-zinc-200 rounded-sm">No exceptions — everything looks clean.</p>}
        </div>
      )}
    </div>
  );
}
